import { configureStore, combineReducers } from '@reduxjs/toolkit';

import projectSlice from './project-slice';
import historySlice from './history-slice';

import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';

const persistConfig = {
  key: 'project',
  storage,
  blacklist: ['currentPackages', 'history'], // Don't persist history
};

// Flag to skip history saves during undo/redo operations
let isUndoRedoInProgress = false;

// Set undo/redo in progress flag (call this before undo/redo to prevent syncing)
export const setUndoRedoInProgress = (inProgress: boolean) => {
  isUndoRedoInProgress = inProgress;
};

// Check if undo/redo is in progress
export const getUndoRedoInProgress = () => isUndoRedoInProgress;

// These are kept for backward compatibility but not needed anymore
export const resetHistorySaveTimer = () => {};
export const markHistorySaveComplete = () => {};

// Combine reducers - project state stays flat at root, history is nested
const rootReducer = combineReducers({
  project: projectSlice,
  history: historySlice,
});

// Custom reducer that spreads project state at root level for backward compatibility
const flattenedReducer = (state: any, action: any) => {
  const combinedState = rootReducer(state ? { project: state, history: state?.history } : undefined, action);
  
  // Flatten: spread project state at root, keep history nested
  return {
    ...combinedState.project,
    history: combinedState.history,
  };
};

const persistedReducer = persistReducer(persistConfig, flattenedReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;